import { Suspense, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { FullPageLoader } from '@/components/common/FullPageLoader'
import { AppDrawer } from './AppDrawer'
import { AppTopBar } from './AppTopBar'

/**
 * Max width of the page content column. On wide viewports the content centres
 * instead of stretching edge to edge; on smaller screens the cap has no effect.
 */
export const PAGE_CONTENT_MAX_WIDTH = '72rem'

/**
 * The authenticated application frame: a fixed top bar, a permission-filtered
 * navigation drawer, and the routed page content.
 *
 * The `<Suspense>` boundary wraps only the page area, so the chrome stays
 * mounted while a code-split page loads.
 */
export function AppShell() {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const [mobileOpen, setMobileOpen] = useState(false)
  const [desktopOpen, setDesktopOpen] = useState(true)
  const location = useLocation()

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  // One trigger, two behaviours: overlay on mobile, collapse/expand on desktop.
  const handleDrawerToggle = () => {
    if (isDesktop) setDesktopOpen((open) => !open)
    else setMobileOpen(true)
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <AppTopBar onToggleDrawer={handleDrawerToggle} />
      <AppDrawer
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        desktopOpen={desktopOpen}
        onExpandDrawer={() => setDesktopOpen(true)}
      />

      <Box component="main" sx={{ flexGrow: 1, minWidth: 0, p: { xs: 2, md: 3 } }}>
        <Toolbar />
        <Box sx={{ width: '100%', maxWidth: PAGE_CONTENT_MAX_WIDTH, mx: 'auto' }}>
          <Suspense fallback={<FullPageLoader label="Loading page…" contained />}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
    </Box>
  )
}
